/// <amd-module name="TFS/Security/Common/Members/SecurityMembers" />
import * as React from "react";
import * as Constants from "TFS/Admin/Common/Constants/Constants";
import { Feature, PerformanceScenarios, ResourceSecurityComponent } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { PermissionsContext } from "TFS/Admin/Common/Contracts/Permissions";
import { PersonaDetailsProvider } from "TFS/Admin/Common/Providers/PersonaDetailsProvider";
import { sendToast } from "TFS/Admin/Common/Util/SendToastUtility";
import { AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { convertGraphMemberToIIdentity, convertIIdentityToGraphMember } from "TFS/Admin/Common/Util/TypeConverters";
import {
    ICustomGraphViewModel,
    IMembersTreeItem,
    ISecurityMembersData,
    ISecurityMembersParentsBucketKey
} from "TFS/Security/Common/Members/Contracts";
import { getResourcesSecurityMembers, updateSecurityInheritPermissions } from "TFS/Security/Common/Members/DataSource/SecurityMembersDataSource";
import * as Resources from "TFS/Security/Common/Members/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { PeoplePickerProvider, IPeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Button } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { IdentityPickerDropdown, IIdentity } from "VSSUI/IdentityPicker";
import { IListSelection, ListSelection } from "VSSUI/List";
import { SingleLayerMasterPanel } from "VSSUI/MasterDetails";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { ITableColumn, SimpleTableCell } from "VSSUI/Table";
import { Toggle } from "VSSUI/Toggle";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn, ITreeRow, Tree } from "VSSUI/TreeEx";
import { Location } from "VSSUI/Utilities/Position";
import { ITreeItem, ITreeItemEx, ITreeItemProvider, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";

export interface ISecurityMembersProps {
    permissionsContext: PermissionsContext;
    onShowMessageCallout: (severity: MessageCardSeverity, message: string) => void;
    onLogTelemetry: (component: ResourceSecurityComponent, action: string, properties?: { [key: string]: any }) => AdminFeatureTelemetryLogger;
    performanceService: IVssPerformanceService;
}

export class SecurityMembers extends VssComponent<ISecurityMembersProps> {
    private _membersData: ISecurityMembersData | undefined;
    private _firstToggle: ObservableValue<boolean>;
    private _selectedItem = new ObservableValue<ICustomGraphViewModel | undefined>(undefined);
    private _itemProvider: ITreeItemProvider<ICustomGraphViewModel> = new TreeItemProvider([]);
    private _selection: IListSelection;
    private _personaDetailsProvider: PersonaDetailsProvider;
    private _clearExplicitPermissions = new ObservableValue(false);
    private _isFiltered: boolean = false;
    private _pickerProvider: IPeoplePickerProvider;
    private _selectedUser: ObservableValue<IIdentity | undefined> = new ObservableValue<IIdentity | undefined>(undefined);

    constructor(props: ISecurityMembersProps, context: IVssComponentContext) {
        super(props, context);

        this._firstToggle = new ObservableValue<boolean>(this.props.permissionsContext.inheritPermissions);
        this._selection = new ListSelection(false);

        this._personaDetailsProvider = new PersonaDetailsProvider(this.context.pageContext);
        this._pickerProvider = new PeoplePickerProvider(this.context.pageContext, {
            identityTypes: [Constants.user, Constants.group],
            operationScopes: this.props.permissionsContext.allowAADSearchInHosted ? [Constants.ims, Constants.aad] : [Constants.ims],
            options: {
                MinResults: Constants.minResults,
                MaxResults: Constants.maxResults
            }
        });
        this._getMembers().then(() => {
            if (this._membersData?.supportServicePrincipals) {
                this._pickerProvider.updateIdentityTypes([Constants.user, Constants.group, Constants.servicePrincipal]);
            }
        });
    }

    public render(): JSX.Element {
        return (
            <div className="flex-row flex-grow security-view-content">
                <SingleLayerMasterPanel
                    renderHeader={this._renderHeader}
                    renderContent={this._renderContent}
                    className="master-panel"
                    showOnSmallScreens={true}
                />

                <Observer selectedItem={this._selectedItem} clearPermissions={this._clearExplicitPermissions}>
                    {(observerProps: { selectedItem: ICustomGraphViewModel; clearPermissions: boolean }) => (
                        <>
                            {observerProps.selectedItem && (
                                <Page className="flex-grow security-permissions-panel">
                                    <CustomHeader className="security-permissions-header">
                                        <HeaderTitleArea>
                                            <div className="flex-grow scroll-hidden">
                                                <FormItem
                                                    className="title-s margin-bottom-8"
                                                    label={
                                                        observerProps.selectedItem.principalName ||
                                                        observerProps.selectedItem.displayName ||
                                                        Resources.PermissionPanelTitle
                                                    }
                                                />
                                            </div>
                                        </HeaderTitleArea>
                                    </CustomHeader>
                                    <WrappedComponent
                                        dependencies={["ms.vss-admin-web.security-permissions-common-content"]}
                                        wrappedType="ms.vss-admin-web.security-permissions-common"
                                        subjectDescriptor={observerProps.selectedItem ? observerProps.selectedItem.descriptor : ""}
                                        permissionsContext={this.props.permissionsContext}
                                        onShowMessageCallout={(severity: MessageCardSeverity, message: string) => {
                                            this.props.onShowMessageCallout(severity, message);
                                        }}
                                        onLogTelemetry={(component: ResourceSecurityComponent, action: string, properties?: { [key: string]: any }) =>
                                            this.props.onLogTelemetry(component, action, properties)
                                        }
                                        clearExplicitPermissions={observerProps.clearPermissions}
                                        permissionsCleared={this._removeMember}
                                        performanceService={this.props.performanceService}
                                        accountName={
                                            observerProps.selectedItem
                                                ? observerProps.selectedItem.mailAddress || observerProps.selectedItem.principalName
                                                : ""
                                        }
                                    />
                                </Page>
                            )}
                        </>
                    )}
                </Observer>
            </div>
        );
    }

    private _renderContent = () => {
        return (
            <Observer selectedItem={this._selectedItem}>
                {(observerProps: { selectedItem: ICustomGraphViewModel }) =>
                    observerProps.selectedItem ? (
                        <Tree<ICustomGraphViewModel>
                            columns={this._mainColumn(observerProps.selectedItem)}
                            itemProvider={this._itemProvider}
                            onToggle={this._onToggle}
                            onSelect={(event, treeItem: ITreeRow<ICustomGraphViewModel>) => {
                                const item = treeItem.data.underlyingItem;

                                if (!item.data.parentKey) {
                                    this._setSelectedItem(item.data);
                                } else if (item.expanded && item.childItems && item.childItems.length > 0) {
                                    this._setSelectedItem(item.childItems[0].data);
                                }
                            }}
                            selection={this._selection}
                            id={"resourceMembers"}
                        />
                    ) : (
                        <FormItem className="no-match" label={this._isFiltered ? Resources.NoMatch : Resources.NoMemberToLoad} />
                    )
                }
            </Observer>
        );
    };

    private _renderHeader = () => {
        return (
            <div className="flex-column">
                <div className="flex-row header-first-row">
                    <div className="flex">
                        {this.props.permissionsContext.canTokenInheritPermissions && (
                            <>
                                <Toggle
                                    className="security-toggle"
                                    text={Resources.InheritanceToggleText}
                                    checked={this._firstToggle}
                                    onChange={this._onInheritanceToggled}
                                    disabled={!this.props.permissionsContext.canManageGroups}
                                />
                                <Icon
                                    ariaLabel={Resources.InheritanceInfo}
                                    iconName="Info"
                                    tooltipProps={{
                                        anchorOffset: { horizontal: 0, vertical: -8 },
                                        showOnFocus: true,
                                        text: Resources.InheritanceInfo,
                                        tooltipOrigin: {
                                            horizontal: Location.center,
                                            vertical: Location.end
                                        }
                                    }}
                                    className="info-icon"
                                    tabIndex={0}
                                />
                            </>
                        )}
                    </div>
                </div>

                <IdentityPickerDropdown
                    className="members-filter"
                    pickerProvider={this._pickerProvider}
                    onChange={this._showEntity}
                    onClear={this._clearSelectedIdentity}
                    suggestionsContainerAriaLabel={Resources.SearchEntityAriaLabel}
                    value={this._selectedUser}
                    placeholder={Resources.SearchEntityAriaLabel}
                    editPlaceholder={Resources.SearchEntityAriaLabel}
                />
            </div>
        );
    };

    private _mainColumn(selectedItem: ICustomGraphViewModel): ITreeColumn<ICustomGraphViewModel>[] {
        const columns: ITreeColumn<ICustomGraphViewModel>[] = [];

        columns.push({
            id: "displayName",
            renderCell: (
                rowIndex: number,
                columnIndex: number,
                tableColumn: ITableColumn<ITreeItemEx<ICustomGraphViewModel>>,
                tableItem: ITreeItemEx<ICustomGraphViewModel>
            ) => this._renderNameCell(rowIndex, columnIndex, tableColumn, tableItem, selectedItem),
            width: new ObservableValue(-90),
            hierarchical: true,
            indentationSize: 0
        });

        columns.push({
            id: "remove",
            renderCell: (
                rowIndex: number,
                columnIndex: number,
                tableColumn: ITableColumn<ITreeItemEx<ICustomGraphViewModel>>,
                tableItem: ITreeItemEx<ICustomGraphViewModel>
            ) => this._renderRemoveCell(rowIndex, columnIndex, tableColumn, tableItem, selectedItem),
            width: new ObservableValue(-10)
        });

        return columns;
    }

    private _renderNameCell = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<ICustomGraphViewModel>,
        treeItem: ITreeItemEx<ICustomGraphViewModel>,
        selectedItem: ICustomGraphViewModel
    ): JSX.Element => {
        const data = treeItem.underlyingItem.data;
        const isParentCell = !data.descriptor;

        const content: JSX.Element = (
            <div className="flex text-ellipsis">
                {!isParentCell && this._renderImageIcon(data, selectedItem)}
                <Tooltip text={data.principalName || data.displayName}>
                    <span className={this._getMainCellClassName(isParentCell)}>{data.displayName}</span>
                </Tooltip>
            </div>
        );

        return ExpandableTreeCell({
            children: content,
            contentClassName: "member-name-column",
            columnIndex,
            treeColumn,
            treeItem
        });
    };

    private _renderRemoveCell = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<ICustomGraphViewModel>,
        treeItem: ITreeItemEx<ICustomGraphViewModel>,
        selectedItem: ICustomGraphViewModel
    ): JSX.Element => {
        let content = <div />;

        if (selectedItem && selectedItem.descriptor && selectedItem.descriptor === treeItem.underlyingItem.data.descriptor) {
            content = (
                <Button
                    ariaLabel={Resources.Remove}
                    iconProps={{ iconName: "Delete" }}
                    onClick={() => {
                        this._clearExplicitPermissions.value = true;
                    }}
                    disabled={!this.props.permissionsContext.canManageGroups || this.props.permissionsContext.hideExplicitClearButton}
                    subtle={true}
                    tooltipProps={{
                        anchorOrigin: { horizontal: Location.center, vertical: Location.end },
                        renderContent: () => {
                            return <div>{Resources.Remove}</div>;
                        }
                    }}
                />
            );
        }

        return SimpleTableCell({
            children: content,
            contentClassName: "no-padding",
            columnIndex
        });
    };

    private _renderImageIcon(graphMember: ICustomGraphViewModel, selectedItem: ICustomGraphViewModel): JSX.Element {
        return (
            <Persona
                size={PersonaSize.size32}
                identity={convertGraphMemberToIIdentity(this.context.pageContext, graphMember)}
                personaProvider={
                    localeIgnoreCaseComparer(graphMember!.descriptor, selectedItem!.descriptor) != 0 ? undefined : this._personaDetailsProvider
                }
                tooltipProps={{
                    showOnFocus: true,
                    text: graphMember.principalName || graphMember.displayName
                }}
                ariaHidden={localeIgnoreCaseComparer(graphMember!.descriptor, selectedItem!.descriptor) != 0}
            />
        );
    }

    private _clearSelectedIdentity = () => {
        this._selectedUser.value = undefined;
        this._getMembers();
    };

    private _getMembers = async () => {
        const telemetryLogger = this.props.onLogTelemetry(ResourceSecurityComponent.SecurityMembers, "Loading members data.");
        this.props.performanceService.startScenario(PerformanceScenarios.LoadMembersData, false);
        this._membersData = await getResourcesSecurityMembers(
            this.context.pageContext,
            this.props.permissionsContext.permissionSetId,
            this.props.permissionsContext.token
        );

        if (this._membersData) {
            this._populateTreeItemProvider(this._membersData.identities);
            this._isFiltered = false;
        } else {
            this.props.onShowMessageCallout(MessageCardSeverity.Error, Resources.UnableToLoadMembers);
            telemetryLogger.captureError("GetMembers: Data provider load failed. Check product trace.");
        }

        this.props.performanceService.endScenario(Feature.ResourceSecurity, PerformanceScenarios.LoadMembersData, false);
    };

    private _populateTreeItemProvider(identities: ICustomGraphViewModel[]) {
        const memberItems: IMembersTreeItem[] = [];

        const bucket = this._getParentSecurityBucket(true);

        identities.forEach((element) => {
            if (localeIgnoreCaseComparer(element.subjectKind, Constants.user) == 0
                || (this._membersData?.supportServicePrincipals && localeIgnoreCaseComparer(element.subjectKind, Constants.servicePrincipal) == 0)) {
                bucket[Constants.user].childItems!.push({ data: element });
            } else {
                if (element.isAadGroup) {
                    bucket[Constants.aadGroup].childItems!.push({ data: element });
                } else {
                    bucket[Constants.group].childItems!.push({ data: element });
                }
            }
        });

        let firstIdentityToSelect: ICustomGraphViewModel | undefined = undefined;

        Object.keys(bucket).forEach((key) => {
            if (bucket[key].childItems!.length > 0) {
                if (!firstIdentityToSelect) {
                    firstIdentityToSelect = bucket[key].childItems![0].data;
                }
                memberItems.push(bucket[key]);
            }
        });

        this._itemProvider = new TreeItemProvider<ICustomGraphViewModel>(memberItems);

        if (firstIdentityToSelect !== undefined) {
            this._selectedItem.value = firstIdentityToSelect;
            this._selection.select(Constants.firstIndex);
        } else {
            this._selectedItem.value = undefined;
            this._selection.clear();
        }
    }

    private _reValidateItemBuckets = () => {
        const rootItems: Readonly<ITreeItem<ICustomGraphViewModel>[]> = this._itemProvider.roots;

        rootItems.forEach((rootItem) => {
            if (!rootItem.childItems || rootItem.childItems.length == 0) {
                this._itemProvider.remove(rootItem);
            }
        });

        if (this._itemProvider.roots.length > 0) {
            // There is at least one child value. So, select the first child.
            this._selectedItem.value = this._itemProvider.value[Constants.firstIndex].underlyingItem.data;
            this._selection.select(Constants.firstIndex);
        } else {
            this._selectedItem.value = undefined;
            this._selection.clear();
        }
    };

    private _removeMember = (descriptor: string) => {
        const telemetryLogger = this.props.onLogTelemetry(ResourceSecurityComponent.SecurityMembers, "Remove member from list");

        this._clearExplicitPermissions.value = false;

        if (this._isFiltered) {
            this._selectedUser.value = undefined;
            this._getMembers();
            return;
        }

        const itemIndex = this._itemProvider.value.findIndex((x) => x.underlyingItem.data.descriptor === descriptor);
        if (itemIndex > -1) {
            const itemToRemove = this._itemProvider.value[itemIndex].underlyingItem;
            this._fetchCurrentMembersAndRemoveMemberIfValid(itemToRemove);
        } else {
            telemetryLogger.captureError("Failed to remove member from list");
        }
    };

    private _fetchCurrentMembersAndRemoveMemberIfValid = async (itemToRemove: ITreeItem<ICustomGraphViewModel>) => {
        const telemetryLogger = this.props.onLogTelemetry(ResourceSecurityComponent.SecurityMembers, "Fetch latest members data.");

        let currentMembersList: ISecurityMembersData | undefined = await getResourcesSecurityMembers(
            this.context.pageContext,
            this.props.permissionsContext.permissionSetId,
            this.props.permissionsContext.token
        );

        if (currentMembersList) {
            const itemIndex = currentMembersList.identities.findIndex((x) => x.descriptor === itemToRemove.data.descriptor);

            if (itemIndex > -1) {
                sendToast(format(Resources.MemberNotRemovedSuccessfully, itemToRemove.data.displayName), this.context.pageContext);
            } else {
                this._itemProvider.remove(itemToRemove);

                if (this._membersData) {
                    this._membersData.identities = this._membersData.identities.filter((element) => {
                        return element.originId != itemToRemove.data.originId;
                    });
                }

                this._reValidateItemBuckets();
                sendToast(format(Resources.MemberRemovedSuccessfully, itemToRemove.data.displayName), this.context.pageContext);
            }
        } else {
            telemetryLogger.captureError("GetMembers: Data provider load failed. Check product trace.");
        }
    };

    private _getMainCellClassName(isParentCell: boolean): string {
        const className = isParentCell ? "fontWeightSemiBold" : "";
        return "capitalize members-text " + className;
    }

    private _onToggle = (event: React.SyntheticEvent<HTMLElement>, treeItem: ITreeItemEx<ICustomGraphViewModel>) => {
        this._itemProvider.toggle(treeItem.underlyingItem);

        if (treeItem.underlyingItem.expanded && treeItem.underlyingItem.childItems && treeItem.underlyingItem.childItems.length > 0) {
            this._setSelectedItem(treeItem.underlyingItem.childItems[0].data);
        } else {
            const firstExpandedRow = this._itemProvider.roots.find((element) => element.expanded === true);

            if (firstExpandedRow && firstExpandedRow.childItems) {
                this._setSelectedItem(firstExpandedRow.childItems[0].data);
            } else {
                this._selection.clear();
            }
        }
    };

    private _onInheritanceToggled = async () => {
        const telemetryLogger = this.props.onLogTelemetry(ResourceSecurityComponent.SecurityMembers, "Inheritance being toggled.");

        const response = await updateSecurityInheritPermissions(
            this.context.pageContext,
            this.props.permissionsContext.permissionSetId,
            this.props.permissionsContext.token,
            !this._firstToggle.value
        );

        if (response && response.statusCode === 204) {
            this._firstToggle.value = !this._firstToggle.value;

            if (this._membersData) {
                // Force an update in this case
                const curItem = this._selectedItem.value;
                this._selectedItem.value = undefined;
                this._selectedItem.value = curItem;
            }
        } else {
            this.props.onShowMessageCallout(MessageCardSeverity.Error, response ? response.statusDescription : Resources.UnableToUpdateInheritance);
            telemetryLogger.captureError("InheritanceToggled: Data provider update failed. Check product trace.");
        }
    };

    private _showEntity = (selectedEntity?: IIdentity) => {
        if (selectedEntity) {
            if (!selectedEntity.localId && selectedEntity.entityType.toLowerCase() !== Constants.user) {
                this.props
                    .onLogTelemetry(ResourceSecurityComponent.SecurityMembers, "Search and update Aad Group.")
                    .captureError("Aad Group update not supported.");
                this.props.onShowMessageCallout(MessageCardSeverity.Error, Resources.AADGroupsNotSupportedMessage);
                return;
            }

            this._selectedUser.value = selectedEntity;
            const identity: ICustomGraphViewModel = convertIIdentityToGraphMember(selectedEntity) as ICustomGraphViewModel;
            this._itemProvider = new TreeItemProvider<ICustomGraphViewModel>([{ data: identity }]);
            this._isFiltered = true;

            this.props.onLogTelemetry(ResourceSecurityComponent.SecurityMembers, "Searched Identity.");

            this._setSelectedItem(identity);
        }
    };

    private _setSelectedItem = (selectedItem: ICustomGraphViewModel): void => {
        const items = this._itemProvider.value;
        for (let i = 0, l = items.length; i < l; i++) {
            if (selectedItem.descriptor === this._itemProvider.value[i].underlyingItem.data.descriptor) {
                this._selection.select(i);
                this._selectedItem.value = selectedItem;
                return;
            }
        }

        // Item not found, clear any existing selection
        this._selection.clear();
        this._selectedItem.value = undefined;
    };

    private _getParentSecurityBucket = (addAll: boolean, key?: string): ISecurityMembersParentsBucketKey => {
        const parents: ISecurityMembersParentsBucketKey = {};

        if (addAll || (key && localeIgnoreCaseComparer(Constants.group, key) == 0)) {
            parents[Constants.group] = {
                data: {
                    displayName: Resources.AzureDevOpsGroups,
                    parentKey: Constants.group,
                    principalName: Resources.AzureDevOpsGroupsTooltip
                } as ICustomGraphViewModel,
                childItems: [] as ITreeItem<ICustomGraphViewModel>[],
                expanded: true
            } as IMembersTreeItem;
        }

        if (addAll || (key && localeIgnoreCaseComparer(Constants.aadGroup, key) == 0)) {
            parents[Constants.aadGroup] = {
                data: {
                    displayName: Resources.AzureActiveDirectoryGroups,
                    parentKey: Constants.aadGroup,
                    principalName: Resources.AzureActiveDirectoryGroupsTooltip
                } as ICustomGraphViewModel,
                childItems: [] as ITreeItem<ICustomGraphViewModel>[],
                expanded: true
            } as IMembersTreeItem;
        }

        if (addAll || (key && localeIgnoreCaseComparer(Constants.user, key) == 0)) {
            parents[Constants.user] = {
                data: { displayName: Resources.Users, parentKey: Constants.user, principalName: Resources.UsersTooltip } as ICustomGraphViewModel,
                childItems: [] as ITreeItem<ICustomGraphViewModel>[],
                expanded: true
            } as IMembersTreeItem;
        }

        return parents;
    };
}

VssComponent.register("ms.vss-admin-web.security-members-common", SecurityMembers);
